import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Button, 
  Alert, 
  Row, 
  Col, 
  InputGroup,
  Spinner 
} from 'react-bootstrap';
import { Eye, EyeOff, User, Mail, Phone, Building, Shield, Edit, Package } from 'lucide-react';
import { userService } from '../../services/userService';
import { boutiqueService } from '../../services/boutiqueService';
import { useAuth } from '../../contexts/AuthContext';

/**
 * ✅ Règles alignées sur UserService.validatePassword() (backend)
 */
const validatePasswordRules = (password) => {
  if (!password || password.length < 6) {
    return 'Le mot de passe doit contenir au moins 6 caractères';
  }
  if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
    return 'Le mot de passe doit contenir au moins une lettre et un chiffre';
  }
  return null;
};

const CreateUser = ({ 
  show, 
  onHide, 
  onUserCreated, 
  selectedBoutiqueId = null, 
  editUser = null 
}) => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [boutiques, setBoutiques] = useState([]);
  const [loadingBoutiques, setLoadingBoutiques] = useState(false);
  const [alert, setAlert] = useState(null);

  // ✅ NOUVEAU: État pour différencier création/modification
  const isEditing = !!editUser;
  const modalTitle = isEditing ? 'Modifier l\'utilisateur' : 'Créer un utilisateur';
  const submitButtonText = isEditing ? 'Mettre à jour' : 'Créer l\'utilisateur';

  // ✅ CORRIGÉ: Initialisation des données selon le mode
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    phone: '',
    password: '',
    role: 'EMPLOYEE',
    boutiqueId: '', // ✅ Laisser vide, sera rempli par useEffect
    productAccessType: 'ALL', // ✅ AJOUT avec valeur par défaut

  });

  const [errors, setErrors] = useState({});

  // ✅ NOUVEAU: Initialiser les données pour l'édition
  useEffect(() => {
    if (show && editUser) {
      setFormData({
        firstName: editUser.first_name || editUser.prenom || '',
        lastName: editUser.last_name || editUser.nom || '',
        email: editUser.email || '',
        username: editUser.username || '',
        phone: editUser.phone || '',
        password: '', // On ne pré-remplit jamais le mot de passe
        role: editUser.role || 'EMPLOYEE',
        boutiqueId: editUser.boutiqueId || editUser.boutique_id || editUser.boutique?.id || '',
        productAccessType: editUser.productAccessType || 'ALL', // ✅ Charger la valeur

      });
    } else if (show && !editUser) {
      // Réinitialiser pour une création
      resetForm();
    }
  }, [show, editUser, selectedBoutiqueId]);

  // ✅ Charger les boutiques au montage
  useEffect(() => {
    if (show && currentUser?.role === 'SUPER_ADMIN') {
      loadBoutiques();
    }
  }, [show, currentUser]);

  // ✅ Fonction pour charger les boutiques
  const loadBoutiques = async () => {
    try {
      setLoadingBoutiques(true);
      
      const response = await boutiqueService.getAllBoutiques();
      
      if (response.success) {
        const boutiquesData = response.data || [];
        setBoutiques(boutiquesData);
        
        // ✅ Sélectionner la première boutique si aucune n'est sélectionnée (mode création)
        if (!isEditing && !selectedBoutiqueId && boutiquesData.length > 0) {
          setFormData(prev => ({
            ...prev,
            boutiqueId: boutiquesData[0].id
          }));
        }
      } else {
        console.error('❌ Erreur chargement boutiques:', response.message);
        setAlert({
          type: 'danger',
          message: 'Erreur lors du chargement des boutiques'
        });
      }
    } catch (error) {
      console.error('❌ Erreur critique boutiques:', error);
      setAlert({
        type: 'danger',
        message: 'Erreur de connexion au serveur'
      });
    } finally {
      setLoadingBoutiques(false);
    }
  };

  // ✅ Gestion des changements de champs
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // ✅ Nettoyer l'erreur du champ modifié
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // ✅ Auto-génération du username (seulement en mode création)
    if (!isEditing && (field === 'firstName' || field === 'lastName')) {
      const firstName = field === 'firstName' ? value : formData.firstName;
      const lastName = field === 'lastName' ? value : formData.lastName;
      
      if (firstName && lastName) {
        const suggestedUsername = `${firstName.toLowerCase()}.${lastName.toLowerCase()}`;
        setFormData(prev => ({
          ...prev,
          username: suggestedUsername
        }));
      }
    }
  };

  // ✅ NOUVEAU: Validation adaptée pour création/modification
  const validateForm = () => {
    const newErrors = {};

    // Validation des champs obligatoires
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Le prénom est obligatoire';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Le nom est obligatoire';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est obligatoire';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Le nom d\'utilisateur est obligatoire';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Le nom d\'utilisateur doit contenir au moins 3 caractères';
    }

    // ✅ NOUVEAU: Mot de passe obligatoire seulement en création
    if (!isEditing) {
            if (!formData.password) {
        newErrors.password = 'Le mot de passe est obligatoire';
      } else {
        // ✅ CORRECTION : aligné sur validatePassword() du backend
        const pwdError = validatePasswordRules(formData.password);
        if (pwdError) newErrors.password = pwdError;
      }
    } else {
      // En modification, si un mot de passe est fourni, il doit être valide
            if (formData.password) {
        // ✅ CORRECTION : même règle qu'en création
        const pwdError = validatePasswordRules(formData.password);
        if (pwdError) newErrors.password = pwdError;
      }
    }

    if (!formData.role) {
      newErrors.role = 'Le rôle est obligatoire';
    }

    // ✅ Validation boutique
    if (currentUser?.role === 'SUPER_ADMIN' && !formData.boutiqueId) {
      newErrors.boutiqueId = 'La boutique est obligatoire';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ NOUVEAU: Soumission adaptée pour création/modification
  const handleSubmit = async (e) => {
  e.preventDefault();
  setAlert(null);

  // ✅ Validation
  if (!validateForm()) {
    setAlert({
      type: 'danger',
      message: 'Veuillez corriger les erreurs dans le formulaire'
    });
    return;
  }

  try {
    setLoading(true);

    // ✅ IMPORTANT: Préparer les données avec boutiqueId
    const dataToSend = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim().toLowerCase(),
      username: formData.username.trim().toLowerCase(),
      phone: formData.phone.trim() || null,
      role: formData.role,
      boutiqueId: formData.boutiqueId, // ✅ CRITIQUE: S'assurer que boutiqueId est envoyé
      productAccessType: formData.productAccessType
    };

    // ✅ Validation côté client: boutiqueId obligatoire pour SUPER_ADMIN
    if (currentUser?.role === 'SUPER_ADMIN' && !dataToSend.boutiqueId) {
      setAlert({
        type: 'danger',
        message: 'La boutique est obligatoire'
      });
      setLoading(false);
      return;
    }

    // ✅ Ajouter le mot de passe seulement si nécessaire
    if (!isEditing || formData.password) {
      dataToSend.password = formData.password;
    }


    let response;
    
    if (isEditing) {
      // ✅ Modification
      response = await userService.updateUser(editUser.id, dataToSend);
    } else {
      // ✅ Création
      response = await userService.createUser(dataToSend);
    }

    if (response.success) {
      
      setAlert({
        type: 'success',
        message: `Utilisateur ${isEditing ? 'modifié' : 'créé'} avec succès !`
      });

      // ✅ Notification au parent
      if (onUserCreated) {
        onUserCreated(response.data);
      }

      // ✅ Fermeture automatique après 1.5 secondes
      setTimeout(() => {
        onHide();
        resetForm();
      }, 1500);

    } else {
      console.error(`❌ Erreur ${isEditing ? 'modification' : 'création'}:`, response.message);
      setAlert({
        type: 'danger',
        message: response.message || `Erreur lors de la ${isEditing ? 'modification' : 'création'} de l'utilisateur`
      });
    }

  } catch (error) {
    console.error(`❌ Erreur critique ${isEditing ? 'modification' : 'création'}:`, error);
    setAlert({
      type: 'danger',
      message: 'Erreur de connexion au serveur'
    });
  } finally {
    setLoading(false);
  }
};

  // ✅ Reset du formulaire
  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      username: '',
      phone: '',
      password: '',
      role: 'EMPLOYEE',
      boutiqueId: selectedBoutiqueId || (boutiques.length > 0 ? boutiques[0].id : ''),
      productAccessType: 'ALL', // ✅ Reset avec valeur par défaut

    });
    setErrors({});
    setAlert(null);
  };

  // ✅ Fermeture du modal
  const handleClose = () => {
    resetForm();
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          {isEditing ? <Edit className="me-2" size={24} /> : <User className="me-2" size={24} />}
          {modalTitle}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="p-4">
          {/* ✅ Affichage des alertes */}
          {alert && (
            <Alert variant={alert.type} className="mb-4">
              {alert.message}
            </Alert>
          )}

          {/* ✅ NOUVEAU: Indicateur de mode */}
          {isEditing && (
            <Alert variant="info" className="mb-4">
              <strong>Mode modification</strong> - Vous modifiez l'utilisateur <strong>{editUser.firstName} {editUser.lastName}</strong>
            </Alert>
          )}

          <Row>
            {/* ✅ Prénom */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Prénom <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <User size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Prénom"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    isInvalid={!!errors.firstName}
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.firstName}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>

            {/* ✅ Nom */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Nom <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <User size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Nom de famille"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    isInvalid={!!errors.lastName}
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.lastName}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            {/* ✅ Email */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <Mail size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="email"
                    placeholder="email@exemple.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    isInvalid={!!errors.email}
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>

            {/* ✅ Téléphone */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">Téléphone</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <Phone size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="tel"
                    placeholder="+221 77 123 45 67"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    disabled={loading}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            {/* ✅ Nom d'utilisateur */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Nom d'utilisateur <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="nom.utilisateur"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  isInvalid={!!errors.username}
                  disabled={loading || isEditing} // ✅ NOUVEAU: Désactivé en modification
                />
                <Form.Control.Feedback type="invalid">
                  {errors.username}
                </Form.Control.Feedback>
                {isEditing && (
                  <Form.Text className="text-muted">
                    Le nom d'utilisateur ne peut pas être modifié
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

            {/* ✅ NOUVEAU: Mot de passe avec gestion modification */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Mot de passe {!isEditing && <span className="text-danger">*</span>}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    placeholder={isEditing ? 'Laisser vide pour ne pas changer' : 'Mot de passe'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    isInvalid={!!errors.password}
                    disabled={loading}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    6 caractères minimum, avec au moins une lettre et un chiffre.
                    {isEditing && ' Laissez vide pour conserver le mot de passe actuel.'}
                  </Form.Text>
                </InputGroup>
                
              </Form.Group>
            </Col>
          </Row>

          <Row>
            {/* ✅ Rôle */}
            
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Rôle <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <Shield size={16} />
                  </InputGroup.Text>
                  <Form.Select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    isInvalid={!!errors.role}
                    disabled={loading}
                  >
                    {/* ✅ Rôles disponibles selon l'utilisateur connecté */}
                    {currentUser?.role === 'SUPER_ADMIN' && (
                      <option value="MANAGER">👑 Gestionnaire</option>
                    )}
                    <option value="EMPLOYEE">👤 Employé</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.role}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>

            {/* ✅ Boutique (Super Admin uniquement) */}
            {currentUser?.role === 'SUPER_ADMIN' && (
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Boutique <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <Building size={16} />
                    </InputGroup.Text>
                    <Form.Select
                      value={formData.boutiqueId}
                      onChange={(e) => handleInputChange('boutiqueId', e.target.value)}
                      isInvalid={!!errors.boutiqueId}
                      disabled={loading || loadingBoutiques}
                    >
                      <option value="">
                        {loadingBoutiques ? 'Chargement...' : 'Sélectionner une boutique'}
                      </option>
                      {boutiques.map(boutique => (
                        <option key={boutique.id} value={boutique.id}>
                          🏪 {boutique.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.boutiqueId}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
            )}

            {/* ✅ NOUVEAU CHAMP : Accès aux types de produits */}
            {(formData.role === 'MANAGER' || formData.role === 'EMPLOYEE') && (
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Accès aux types de produits <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <Package size={16} />
                    </InputGroup.Text>
                    <Form.Select
                      value={formData.productAccessType}
                      onChange={(e) => handleInputChange('productAccessType', e.target.value)}
                      disabled={loading}
                    >
                      <option value="ALL">📦🔧 Tous les produits (Stockables + Services)</option>
                      <option value="STOCKABLE">📦 Produits stockables uniquement</option>
                      <option value="SERVICE">🔧 Services uniquement</option>
                    </Form.Select>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Définit les types de produits que cet utilisateur pourra gérer et vendre
                  </Form.Text>
                </Form.Group>
              </Col>
            )}
          </Row>
        </Modal.Body>

        <Modal.Footer className="bg-light">
          <Button 
            variant="outline-secondary" 
            onClick={handleClose}
            disabled={loading}
          >
            Annuler
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {isEditing ? 'Modification...' : 'Création en cours...'}
              </>
            ) : (
              <>
                {isEditing ? <Edit className="me-2" size={16} /> : <User className="me-2" size={16} />}
                {submitButtonText}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateUser;